import {
	updatePropertyFn,
	updateValuesWrapper,
	useMultiplePropUpdates,
} from "@repo/dynamicEditor/src/helpers/updatePropertiesHooks";
import { PushProcessingNotification } from "@repo/helpers/src/notificationActions";
import { keyPair } from "@repo/models";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";

import { useLocationApi, useLocationQueries } from "../../api/locationApi";
import { getItems, useFilterItemQuery } from "../../api/queries";
import * as Queries from "../../api/queries";
import { EditableItemDynamicTab } from "../../components/dynamicEditor/dynamicTab/editableItem";
import { ITemplateData } from "../../components/dynamicEditor/templateDataHook";
import { useLocationContext, useStatusHistoryContext } from "../../contexts/editableItemContext";
import { tabs } from "../../string";

export const StatusHistoryEditor: FC = () => {
	const locationContext = useLocationContext();
	const statusHistoryContext = useStatusHistoryContext();
	const locationApi = useLocationApi();
	const { locationId } = locationContext;
	const { item: statusHistory } = statusHistoryContext;
	const [activeInfoLists, activeLocationInfoListsLoaded] = useLocationInfoLists();
	const { useStatusHistoriesQuery } = useLocationQueries();
	const { clearQuery: clearStatusHistoriesQuery } = useStatusHistoriesQuery(locationId);
	const queryClient = useQueryClient();

	// Stable identity: useTemplateData's effect remaps the whole editor when this changes
	const templateData = useMemo(() => {
		return {
			activeInfoLists,
		};
	}, [activeInfoLists]);

	const loaded = activeLocationInfoListsLoaded;

	async function updateValueInternal(
		id: string,
		value: any,
		currentState: ILocationInfoData,
		parentComponent?: string,
	) {
		const { newState } = await updateValues(id, value, currentState, parentComponent);
		return (newState ?? currentState) as ILocationInfoData;
	}
	const customUpdates = useMultiplePropUpdates(applyBusinessLogic) as updatePropertyFn<ILocationInfoData>;

	const updateValues = updateValuesWrapper<ILocationInfoData>(customUpdates);

	async function applyBusinessLogic(id: string, value: any, newState: ILocationInfoData) {
		async function update(name: string, value: any) {
			newState = await updateValueInternal(name, value, newState);
		}

		const clear = async (name: string) => await update(name, null);

		switch (id) {
			case "statusHistory.statusChanged":
				await update("statusHistory.startDate", new Date());
				break;
		}
		return newState as ILocationInfoData;
	}

	async function save() {
		const notify = PushProcessingNotification("Saving Location Status...");
		try {
			const error = await locationApi.updateLocationStatus(locationId, statusHistory);
			await clearStatusHistoriesQuery();
			await queryClient.invalidateQueries({ queryKey: [Queries.GROUP.LOCATION] });

			if (error) {
				notify.error(error);
				return;
			}

			notify.success("Location Status Saved");

			return statusHistory;
		} catch (ex) {
			notify.error("Failed to save location");
			throw ex;
		}
	}

	return (
		<EditableItemDynamicTab
			itemContext={statusHistoryContext}
			pageName={tabs.statusHistory}
			otherTemplateData={templateData}
			itemProp="statusHistory"
			updateValues={updateValues}
			save={save}
			loaded={loaded}
		/>
	);
};

interface ILocationInfoData extends ITemplateData {
	activeInfoLists?: ILocationInfoLists;
}

interface ILocationInfoLists {
	statuses: keyPair[];
}

function useLocationInfoLists() {
	const { locationLoaded } = useLocationContext();
	const { getLocationStatuses } = useLocationApi();

	const [locationStatuses, locationStatusesLoaded] = useFilterItemQuery(
		Queries.PORTAL.LOCATION_STATUSES,
		getLocationStatuses,
	);

	const loaded = locationLoaded && locationStatusesLoaded;

	const lists = useMemo(() => {
		return {
			statuses: getItems(locationStatuses, locationStatusesLoaded),
		} as ILocationInfoLists;
	}, [locationLoaded, locationStatuses, locationStatusesLoaded]);

	return [lists, loaded] as const;
}
